import { isJustGoDomain } from '../_constants/domain-config';

const TRACKING_GCLID_STORAGE_KEY = 'tracking_gclid';
const TRACKING_UTM_STORAGE_PREFIX = 'tracking_';
const UTM_PARAMETERS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;
const JUSTGO_MEASUREMENT_ID = 'G-8N9FV5KSQ1';
const JUSTGO_SESSION_COOKIE_NAME = '_ga_8N9FV5KSQ1';

function getCookieMap(): Record<string, string> {
  if (typeof document === 'undefined' || !document.cookie) {
    return {};
  }

  return document.cookie.split('; ').reduce((cookies, entry) => {
    const separatorIndex = entry.indexOf('=');
    if (separatorIndex < 0) {
      return cookies;
    }

    const key = entry.slice(0, separatorIndex);
    const value = decodeURIComponent(entry.slice(separatorIndex + 1));
    cookies[key] = value;
    return cookies;
  }, {} as Record<string, string>);
}

function getReferrerDomain(referrer: string): string {
  if (!referrer) {
    return '';
  }

  try {
    return new URL(referrer).origin;
  } catch {
    return '';
  }
}

function getSessionIdFromCookies(cookies: Record<string, string>): number {
  const justGoSessionCookie = cookies[JUSTGO_SESSION_COOKIE_NAME];
  const fallbackSessionCookie = Object.entries(cookies)
    .find(([key, value]) => key.startsWith('_ga_') && value.startsWith('GS'))?.[1];
  const sessionCookie = justGoSessionCookie || fallbackSessionCookie;

  if (!sessionCookie) {
    return 0;
  }

  const parts = sessionCookie.split('.');
  const sessionIdMatch = parts.length > 2 ? parts[2].match(/^s?(\d+)/) : null;
  return sessionIdMatch ? Number.parseInt(sessionIdMatch[1], 10) : 0;
}

function extractGclidFromCookieValue(value: string): string {
  if (!value) {
    return '';
  }

  const parts = value.split('.');
  return parts.length >= 3 ? parts[2] : parts[parts.length - 1] || '';
}

function getPersistedGclid(queryGclid: string, cookieGclid: string): string {
  if (typeof sessionStorage === 'undefined') {
    return queryGclid || cookieGclid || '';
  }

  if (queryGclid) {
    sessionStorage.setItem(TRACKING_GCLID_STORAGE_KEY, queryGclid);
    return queryGclid;
  }

  if (cookieGclid) {
    sessionStorage.setItem(TRACKING_GCLID_STORAGE_KEY, cookieGclid);
    return cookieGclid;
  }

  return sessionStorage.getItem(TRACKING_GCLID_STORAGE_KEY) || '';
}

function getEngagementTimeMsec(): number {
  if (typeof window === 'undefined' || !window.performance || typeof window.performance.now !== 'function') {
    return 0;
  }

  return Math.max(1, Math.round(window.performance.now()));
}

export function captureTrackingParams(): void {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }

  const queryParams = new URLSearchParams(window.location.search);
  [...UTM_PARAMETERS, 'gclid'].forEach(parameter => {
    const value = queryParams.get(parameter);
    if (value) {
      sessionStorage.setItem(`${TRACKING_UTM_STORAGE_PREFIX}${parameter}`, value);
    }
  });
}

function getPersistedUtmParameter(parameter: typeof UTM_PARAMETERS[number]): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const queryValue = new URLSearchParams(window.location.search).get(parameter) || '';
  if (typeof sessionStorage === 'undefined') {
    return queryValue;
  }

  return queryValue || sessionStorage.getItem(`${TRACKING_UTM_STORAGE_PREFIX}${parameter}`) || '';
}

export function buildTrackingInfo(referrer: string | null) {
  captureTrackingParams();
  const cookies = getCookieMap();
  const queryGclid = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('gclid') || ''
    : '';
  const cookieGclid = extractGclidFromCookieValue(cookies._gcl_aw || cookies._gcl_dc || cookies.gclid || '');

  return {
    external_referrer_domain: getReferrerDomain(referrer || ''),
    external_referrer_url: referrer || '',
    campaign_id: '',
    country_landing_page_source: '',
    utm_source: getPersistedUtmParameter('utm_source'),
    utm_medium: getPersistedUtmParameter('utm_medium'),
    utm_campaign: getPersistedUtmParameter('utm_campaign'),
    client_id: cookies._ga || '',
    session_id: getSessionIdFromCookies(cookies),
    gclid: getPersistedGclid(queryGclid, cookieGclid),
    measurement_id: typeof window !== 'undefined' && isJustGoDomain() ? JUSTGO_MEASUREMENT_ID : '',
    page_location: typeof window !== 'undefined' ? window.location.href : '',
    engagement_time_msec: getEngagementTimeMsec()
  };
}
